package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PlannerGoalType
import com.example.positionahead.model.PortfolioObjective
import com.example.positionahead.model.RiskTolerance
import com.example.positionahead.model.effectiveBondTarget
import com.example.positionahead.model.effectiveStockTarget
import com.example.positionahead.model.objectiveLabel
import com.example.positionahead.model.plannerGoalLabel
import com.example.positionahead.model.progressPercent
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatPercent

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GoalsScreen(
    goal: FinancialGoal,
    currentPortfolioValue: Double,
    onSaveGoal: (
        plannerGoalType: PlannerGoalType,
        customGoalLabel: String?,
        targetAmount: Double,
        targetDateYears: Int,
        monthlyContribution: Double,
        age: Int,
        riskTolerance: RiskTolerance,
        objective: PortfolioObjective,
        maxSinglePositionPercent: Double,
        targetYieldPercent: Double,
        investmentHorizonYears: Int,
    ) -> Unit,
    modifier: Modifier = Modifier,
    embedded: Boolean = false,
) {
    var plannerGoalType by remember(goal) { mutableStateOf(goal.plannerGoalType) }
    var customLabel by remember(goal) { mutableStateOf(goal.customGoalLabel.orEmpty()) }
    var targetAmount by remember(goal) { mutableStateOf(goal.targetAmount.toLong().toString()) }
    var targetYears by remember(goal) { mutableStateOf(goal.targetDateYears.toString()) }
    var monthlyContribution by remember(goal) { mutableStateOf(goal.monthlyContribution.toLong().toString()) }
    var age by remember(goal) { mutableStateOf(goal.age.toString()) }
    var riskTolerance by remember(goal) { mutableStateOf(goal.riskTolerance) }
    var objective by remember(goal) { mutableStateOf(goal.objective) }
    var maxPosition by remember(goal) { mutableStateOf(goal.maxSinglePositionPercent.toString()) }
    var targetYield by remember(goal) { mutableStateOf(goal.targetYieldPercent.toString()) }
    var horizon by remember(goal) { mutableStateOf(goal.investmentHorizonYears.toString()) }
    var plannerExpanded by remember { mutableStateOf(false) }
    var riskExpanded by remember { mutableStateOf(false) }
    var objectiveExpanded by remember { mutableStateOf(false) }

    val previewGoal = FinancialGoal(
        plannerGoalType = plannerGoalType,
        customGoalLabel = customLabel.takeIf { it.isNotBlank() },
        targetAmount = targetAmount.toDoubleOrNull() ?: goal.targetAmount,
        targetDateYears = targetYears.toIntOrNull() ?: goal.targetDateYears,
        monthlyContribution = monthlyContribution.toDoubleOrNull() ?: goal.monthlyContribution,
        age = age.toIntOrNull() ?: goal.age,
        riskTolerance = riskTolerance,
        objective = objective,
        maxSinglePositionPercent = maxPosition.toDoubleOrNull() ?: goal.maxSinglePositionPercent,
        targetYieldPercent = targetYield.toDoubleOrNull() ?: goal.targetYieldPercent,
        investmentHorizonYears = horizon.toIntOrNull() ?: goal.investmentHorizonYears,
    )
    val progress = previewGoal.progressPercent(currentPortfolioValue)

    Column(
        modifier = modifier
            .then(if (embedded) Modifier.fillMaxWidth() else Modifier.fillMaxSize().verticalScroll(rememberScrollState()))
            .padding(if (embedded) 0.dp else 16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        if (!embedded) {
            Text("Financial planner goals", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
            Text(
                "Define what you're building toward. Discover and recommendations use these goals — no advisor required.",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.75f),
            )
        }

        Text("${previewGoal.plannerGoalLabel()} progress", style = MaterialTheme.typography.titleMedium)
        LinearProgressIndicator(
            progress = { (progress / 100.0).toFloat().coerceIn(0f, 1f) },
            modifier = Modifier.fillMaxWidth(),
        )
        Text(
            "${formatCurrency(currentPortfolioValue)} of ${formatCurrency(previewGoal.targetAmount)} (${formatPercent(progress, decimals = 0)})",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.primary,
        )
        Text(
            "${previewGoal.monthlyContribution.toLong()} / month · ${previewGoal.targetDateYears} years to target",
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        )

        ExposedDropdownMenuBox(expanded = plannerExpanded, onExpandedChange = { plannerExpanded = it }) {
            OutlinedTextField(
                value = previewGoal.plannerGoalLabel(),
                onValueChange = {},
                readOnly = true,
                label = { Text("Life goal") },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(plannerExpanded) },
                modifier = Modifier.fillMaxWidth().menuAnchor(),
            )
            ExposedDropdownMenu(expanded = plannerExpanded, onDismissRequest = { plannerExpanded = false }) {
                PlannerGoalType.entries.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.name.replace('_', ' ')) },
                        onClick = {
                            plannerGoalType = option
                            plannerExpanded = false
                        },
                    )
                }
            }
        }

        if (plannerGoalType == PlannerGoalType.CUSTOM) {
            OutlinedTextField(
                value = customLabel,
                onValueChange = { customLabel = it },
                label = { Text("Custom goal name") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )
        }

        OutlinedTextField(
            value = targetAmount,
            onValueChange = { targetAmount = it.filter { c -> c.isDigit() || c == '.' } },
            label = { Text("Target amount ($)") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )
        OutlinedTextField(
            value = targetYears,
            onValueChange = { targetYears = it.filter { c -> c.isDigit() } },
            label = { Text("Years to reach target") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )
        OutlinedTextField(
            value = monthlyContribution,
            onValueChange = { monthlyContribution = it.filter { c -> c.isDigit() || c == '.' } },
            label = { Text("Monthly contribution ($)") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )

        Text("Investment profile", style = MaterialTheme.typography.titleMedium)

        OutlinedTextField(
            value = age,
            onValueChange = { age = it.filter { c -> c.isDigit() } },
            label = { Text("Your age") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )

        ExposedDropdownMenuBox(expanded = objectiveExpanded, onExpandedChange = { objectiveExpanded = it }) {
            OutlinedTextField(
                value = objective.name.replace('_', ' '),
                onValueChange = {},
                readOnly = true,
                label = { Text("Strategy") },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(objectiveExpanded) },
                modifier = Modifier.fillMaxWidth().menuAnchor(),
            )
            ExposedDropdownMenu(expanded = objectiveExpanded, onDismissRequest = { objectiveExpanded = false }) {
                PortfolioObjective.entries.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.name.replace('_', ' ')) },
                        onClick = {
                            objective = option
                            objectiveExpanded = false
                        },
                    )
                }
            }
        }

        ExposedDropdownMenuBox(expanded = riskExpanded, onExpandedChange = { riskExpanded = it }) {
            OutlinedTextField(
                value = riskTolerance.name,
                onValueChange = {},
                readOnly = true,
                label = { Text("Risk tolerance") },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(riskExpanded) },
                modifier = Modifier.fillMaxWidth().menuAnchor(),
            )
            ExposedDropdownMenu(expanded = riskExpanded, onDismissRequest = { riskExpanded = false }) {
                RiskTolerance.entries.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.name) },
                        onClick = {
                            riskTolerance = option
                            riskExpanded = false
                        },
                    )
                }
            }
        }

        OutlinedTextField(
            value = maxPosition,
            onValueChange = { maxPosition = it },
            label = { Text("Max single position (%)") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )
        OutlinedTextField(
            value = targetYield,
            onValueChange = { targetYield = it },
            label = { Text("Target annual yield (%)") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )
        OutlinedTextField(
            value = horizon,
            onValueChange = { horizon = it.filter { c -> c.isDigit() } },
            label = { Text("Investment horizon (years)") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )

        Text(
            "${previewGoal.objectiveLabel()} allocation: ${previewGoal.effectiveStockTarget()}% stocks / ${previewGoal.effectiveBondTarget()}% bonds",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.primary,
        )

        Button(
            onClick = {
                onSaveGoal(
                    plannerGoalType,
                    customLabel.takeIf { it.isNotBlank() },
                    targetAmount.toDoubleOrNull() ?: goal.targetAmount,
                    targetYears.toIntOrNull() ?: goal.targetDateYears,
                    monthlyContribution.toDoubleOrNull() ?: goal.monthlyContribution,
                    age.toIntOrNull() ?: goal.age,
                    riskTolerance,
                    objective,
                    maxPosition.toDoubleOrNull() ?: goal.maxSinglePositionPercent,
                    targetYield.toDoubleOrNull() ?: goal.targetYieldPercent,
                    horizon.toIntOrNull() ?: goal.investmentHorizonYears,
                )
            },
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text("Save goals & refresh ideas")
        }
    }
}
